import { FORGD_SYSTEM_PROJECT_ID } from '@forgd/shared/constants'

/**
 * Used by projectId middleware and module
 */
export const routesWithoutProjectId = [
  '/share', // Token Designer public page
  '/research',
  '/market-maker-leaderboard',
  '/universal-token-rating',
  '/advisor-portal',
  '/academy',
  '/blog',
  '/auth',
  '/onboarding',
  '/support',
  '/consultation',
  '/about',
  '/login',
  '/register',
  '/v2/authorization',
  '/v2/invitation',
  '/confirm-email',
  '/email-redirect',
  '/forgot-password',
  '/magic-link',
  '/page-not-found',
  '/reset-password',
  '/token-expired',
  '/verify-email',
  '/terms-of-use',
  '/terms-conditions',
  '/privacy-policy',
]

export function isRouteWithoutProjectId(path: string) {
  // vue-router matches case-insensitively, so /About/pricing still renders the /about/pricing page.
  const normalizedPath = path.toLowerCase()
  return routesWithoutProjectId.some(route => normalizedPath === route || normalizedPath.startsWith(`${route}/`))
}

/**
 * Anonymous visitors of these routes are sent to the landing page instead of login.
 */
export const landingRedirectRoutes = [
  '/settings',
  '/token-settings',
]

export interface ProjectRouteContext {
  path: string
  allowPreLogin: boolean
  preservePublicPath?: boolean
  fromProjectId?: string
  authProjectId?: string
  isLoggedIn: boolean
}

export type ProjectRouteResolution =
  | { action: 'proceed' }
  | { action: 'scope', path: string }
  | { action: 'login' }
  | { action: 'landing' }

/**
 * Decides what the project-id global middleware does with a navigation.
 *
 * Routes outside `routesWithoutProjectId` are project-scoped: when a projectId
 * is available (URL of the previous route first, then the selected project) the
 * navigation is rewritten to the `/project/:projectId` variant. This includes
 * `allowPreLogin` - only means the page may render without a project when
 * none is available (e.g. anonymous visitors), not that project scoping is skipped.
 * `preservePublicPath` keeps anonymous visitors on the bare public path; logged-in
 * users are still scoped to their project.
 */
export function resolveProjectRoute(ctx: ProjectRouteContext): ProjectRouteResolution {
  if (ctx.path.startsWith('/project/') || isRouteWithoutProjectId(ctx.path)) {
    return { action: 'proceed' }
  }

  if (ctx.allowPreLogin && ctx.preservePublicPath && !ctx.isLoggedIn) {
    return { action: 'proceed' }
  }

  if (!ctx.isLoggedIn && landingRedirectRoutes.some(route => ctx.path === route || ctx.path.startsWith(`${route}/`))) {
    return { action: 'landing' }
  }

  // The system demo project never scopes a route — the MMM middleware redirects
  // its scoped URLs back to the bare path, which would loop.
  const fromProjectId = ctx.fromProjectId === FORGD_SYSTEM_PROJECT_ID ? undefined : ctx.fromProjectId
  const projectId = fromProjectId || ctx.authProjectId
  if (projectId) {
    const path = ctx.path === '/' ? `/project/${projectId}` : `/project/${projectId}${ctx.path}`
    return { action: 'scope', path }
  }

  if (ctx.allowPreLogin) {
    return { action: 'proceed' }
  }

  return ctx.isLoggedIn ? { action: 'proceed' } : { action: 'login' }
}
